import React, { useEffect, useState } from 'react';
import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import SupportAgentRoundedIcon from '@mui/icons-material/SupportAgentRounded';
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

import { fetchMailCenterOverview } from '../../api/mailCenter';

function formatDateTime(value) {
  if (!value) {
    return 'n/a';
  }
  return new Date(value).toLocaleString('fr-FR');
}

function SummaryCard({ icon, label, value, detail, background }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 4,
        minHeight: 160,
        background,
        border: '1px solid rgba(23,50,60,0.08)',
      }}
    >
      <Stack spacing={1.2}>
        <Box sx={{ width: 44, height: 44, borderRadius: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(255,255,255,0.75)', color: '#17323c' }}>
          {icon}
        </Box>
        <Typography variant="caption" sx={{ color: '#4f646f', letterSpacing: 0.8, textTransform: 'uppercase', fontWeight: 800 }}>
          {label}
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 900, color: '#17323c' }}>
          {value}
        </Typography>
        <Typography variant="body2" sx={{ color: '#4f646f', lineHeight: 1.6 }}>
          {detail}
        </Typography>
      </Stack>
    </Paper>
  );
}

function NotificationList({ title, items, emptyLabel, color }) {
  return (
    <Paper sx={{ p: 3, borderRadius: 5, background: '#fff' }}>
      <Typography variant="h6" sx={{ fontWeight: 800, color: '#17323c', mb: 2 }}>
        {title}
      </Typography>
      {items.length === 0 ? (
        <Typography variant="body2" sx={{ color: '#6b7c84' }}>
          {emptyLabel}
        </Typography>
      ) : (
        <Stack spacing={1.5}>
          {items.map((item) => (
            <Box key={item.id} sx={{ p: 2, borderRadius: 3, background: `${color}12`, border: `1px solid ${color}22` }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between">
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color, mb: 0.4 }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#4f646f', lineHeight: 1.6 }}>
                    {item.message}
                  </Typography>
                </Box>
                <Chip
                  size="small"
                  label={`${item.occurrenceCount || 1} occurrence${item.occurrenceCount > 1 ? 's' : ''}`}
                  sx={{ bgcolor: 'rgba(255,255,255,0.7)', alignSelf: 'flex-start' }}
                />
              </Stack>
              <Typography variant="caption" sx={{ color: '#6b7c84', display: 'block', mt: 1.2 }}>
                {formatDateTime(item.lastOccurredAt)}
              </Typography>
            </Box>
          ))}
        </Stack>
      )}
    </Paper>
  );
}

const AdminOverviewPage = () => {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  const loadOverview = async (showSpinner = true) => {
    if (showSpinner) {
      setLoading(true);
    } else {
      setRefreshing(true);
    }
    setError('');

    try {
      const payload = await fetchMailCenterOverview();
      setOverview(payload);
    } catch (loadError) {
      setError(loadError.message || 'Impossible de charger la vue d’ensemble admin.');
      setOverview(null);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadOverview(true);
  }, []);

  return (
    <Box sx={{ p: 4, background: 'linear-gradient(180deg, #f4efe6 0%, #eef3f6 100%)', minHeight: '100%' }}>
      <Stack spacing={3}>
        <Paper sx={{ p: 3.5, borderRadius: 5, background: 'linear-gradient(135deg, #17323c 0%, #355b63 100%)', color: '#f7f3ea' }}>
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2.5} justifyContent="space-between" alignItems={{ xs: 'stretch', lg: 'center' }}>
            <Box sx={{ maxWidth: 780 }}>
              <Chip label="Vue d'ensemble" sx={{ bgcolor: 'rgba(247,243,234,0.14)', color: '#f7f3ea', fontWeight: 700, mb: 1.5 }} />
              <Typography variant="h4" sx={{ fontWeight: 800, mb: 1.2 }}>
                Cockpit administratif du Mail Center
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(247,243,234,0.82)', lineHeight: 1.7 }}>
                Cette vue centralise les tickets de contact non lus, les notifications en attente et les incidents récents qui nécessitent un suivi administrateur.
              </Typography>
            </Box>

            <Button
              variant="contained"
              onClick={() => loadOverview(false)}
              disabled={loading || refreshing}
              startIcon={refreshing ? <CircularProgress size={18} color="inherit" /> : <RefreshRoundedIcon />}
              sx={{ bgcolor: '#f3c65c', color: '#17323c', fontWeight: 800, '&:hover': { bgcolor: '#e7b94f' } }}
            >
              Actualiser
            </Button>
          </Stack>
        </Paper>

        {error ? <Alert severity="error">{error}</Alert> : null}

        {loading && !overview ? (
          <Paper sx={{ p: 5, borderRadius: 5, textAlign: 'center' }}>
            <CircularProgress sx={{ color: '#17323c', mb: 2 }} />
            <Typography variant="body2" sx={{ color: '#4f646f' }}>
              Chargement des indicateurs administratifs...
            </Typography>
          </Paper>
        ) : (
          <>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2.2}>
              <SummaryCard
                icon={<DashboardRoundedIcon />}
                label="Messages contact non lus"
                value={overview?.unreadContactMessages ?? 0}
                detail="Nombre de tickets encore à ouvrir dans le Mail Center."
                background="linear-gradient(135deg, rgba(243,198,92,0.30) 0%, rgba(255,255,255,0.92) 100%)"
              />
              <SummaryCard
                icon={<NotificationsActiveRoundedIcon />}
                label="Notifications non lues"
                value={overview?.unreadNotifications ?? 0}
                detail="Regroupe warnings, erreurs et incidents critiques non traités."
                background="linear-gradient(135deg, rgba(125,208,211,0.26) 0%, rgba(255,255,255,0.92) 100%)"
              />
            </Stack>

            <Stack direction={{ xs: 'column', xl: 'row' }} spacing={2.2} alignItems="stretch">
              <Paper sx={{ p: 3, borderRadius: 5, background: '#fff', flex: 1 }}>
                <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 2 }}>
                  <SupportAgentRoundedIcon sx={{ color: '#17323c' }} />
                  <Typography variant="h6" sx={{ fontWeight: 800, color: '#17323c' }}>
                    Widget support
                  </Typography>
                </Stack>
                <Divider sx={{ mb: 2 }} />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                  <Chip label={`Nouveau: ${overview?.contacts?.newCount ?? 0}`} sx={{ bgcolor: 'rgba(243,198,92,0.18)', fontWeight: 800 }} />
                  <Chip label={`En cours: ${overview?.contacts?.inProgressCount ?? 0}`} sx={{ bgcolor: 'rgba(125,208,211,0.18)', fontWeight: 800 }} />
                </Stack>
              </Paper>

              <Paper sx={{ p: 3, borderRadius: 5, background: '#fff', flex: 1 }}>
                <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 2 }}>
                  <NotificationsActiveRoundedIcon sx={{ color: '#17323c' }} />
                  <Typography variant="h6" sx={{ fontWeight: 800, color: '#17323c' }}>
                    Widget notifications
                  </Typography>
                </Stack>
                <Divider sx={{ mb: 2 }} />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} flexWrap="wrap" useFlexGap>
                  <Chip label={`Warnings: ${overview?.notifications?.warningCount ?? 0}`} sx={{ bgcolor: 'rgba(243,198,92,0.18)', fontWeight: 800 }} />
                  <Chip label={`Erreurs: ${overview?.notifications?.errorCount ?? 0}`} sx={{ bgcolor: 'rgba(228,104,82,0.18)', fontWeight: 800 }} />
                  <Chip label={`Critiques: ${overview?.notifications?.criticalCount ?? 0}`} sx={{ bgcolor: 'rgba(181,46,46,0.18)', fontWeight: 800 }} />
                </Stack>
              </Paper>
            </Stack>

            <Stack direction={{ xs: 'column', xl: 'row' }} spacing={2.2} alignItems="flex-start">
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <NotificationList
                  title="Erreurs récentes"
                  items={overview?.recentErrors || []}
                  emptyLabel="Aucune erreur récente enregistrée."
                  color="#b35d2d"
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <NotificationList
                  title="Événements critiques récents"
                  items={overview?.recentCriticalEvents || []}
                  emptyLabel="Aucun événement critique récent."
                  color="#a43e2a"
                />
              </Box>
            </Stack>
          </>
        )}
      </Stack>
    </Box>
  );
};

export default AdminOverviewPage;